import store from 'store';
import axios from 'axios';

/* eslint no-param-reassign:
  ["error", {
    "props": true,
    "ignorePropertyModificationsFor": ["config"],
  }
] */

/**
 * Default configuration
 */

const API_URL = process.env.VUE_APP_API;

const defaults = {
  baseUrl: API_URL,
  tokenHeader: 'Authorization',
  tokenType: 'Bearer',
  tokenName: 'oauthTokens',
  requestDataKey: 'data',
  responseDataKey: 'data',

  /**
   * Default request interceptor for Axios library
   * @context OAuthProvider
   */
  bindRequestInterceptor($auth) {
    const { tokenHeader } = $auth.options;

    $auth.$http.interceptors.request.use((config) => {
      if ($auth.isAuthenticated()) {
        config.headers[tokenHeader] = [
          $auth.options.tokenType, $auth.getToken(),
        ].join(' ');
      } else {
        delete config.headers[tokenHeader];
      }
      return config;
    });
  },

  /**
   * Default response interceptor for Axios library
   * @context OAuthProvider
   */
  bindResponseInterceptor($auth) {
    $auth.$http.interceptors.response.use((response) => {
      $auth.setToken(response);
      return response;
    });
  },
};

function OAuthProvider($http, opts) {
  let options = Object.assign({}, defaults);
  options = Object.assign(options, opts);

  Object.defineProperties(this, {
    state: {
      get() {
        return {};
      },
    },
    store: {
      get() {
        return store;
      },
    },
    options: {
      get() {
        return options;
      },
    },
    $http: {
      get() {
        return axios;
      },
    },
  });
}

/**
 * Login
 *
 * Login and store tokens
 * @return Promise - thenable tokens
 */
OAuthProvider.prototype.authenticate = function authenticate(email, password) {
  this.state.userEmail = email;
  return this.$http({
    method: 'post',
    url: `${this.options.baseUrl}/partners/login`,
    data: {
      email,
      password,
    },
  }).then((response) => {
    if (response.data.error === false) {
      const { permissions, partner, data } = response.data;
      const token = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        tokenType: data.token_type,
        expiresIn: data.expires_in,
        partner,
        permissions,
      };
      token.isAuthenticated = this.validateToken(token.accessToken);
      this.store.set(this.options.tokenName, token);
      this.store.set('partner', partner);
      this.store.set('permissions', permissions);
      return token;
    }

    return false;
  });
};

/**
 * RefreshToken
 *
 * Refreshes token when is expired
 * @param token - accepts token paramter
 * @return token
 */
OAuthProvider.prototype.refreshToken = function refreshToken() {};

/**
 * SetToken
 *
 * Sets token in browserstorage
 * @param response - method is used in the BindInterceptors
 */
OAuthProvider.prototype.setToken = function setToken(response) {
  let resp = response;
  let token;
  if (response[this.options.responseDataKey]) {
    resp = response[this.options.responseDataKey];
  }

  if (resp.access_token) {
    token = response.access_token;
  }

  if (!token && resp) {
    token = resp[this.options.tokenName];
  }

  if (token) {
    this.store.set(this.options.tokenName, token);
  }
};

/**
 * ValidateToken
 *
 * Validates Laravel passports JWT token - format is XXX.YYY.ZZZ
 * @return boolean
 */
OAuthProvider.prototype.validateToken = function validateToken(token) {
  if (token.split('.').length === 3) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace('-', '+').replace('_', '/');
      const expiry = JSON.parse(window.atob(base64)).exp;
      if (typeof expiry === 'number') {
        return Math.round(new Date().getTime() / 1000) < expiry;
      }
    } catch (e) {
      return false;
    }
  }
  return false;
};

/**
 * IsAuthenticated
 *
 * @return boolean - Authenticated
 */
OAuthProvider.prototype.isAuthenticated = function isAuthenticated() {
  const storage = this.store.get(this.options.tokenName);
  if (!storage) {
    return false;
  }
  if (!storage.accessToken) {
    return false;
  }
  return this.validateToken(storage.accessToken);
};

/**
 * GetToken
 *
 * Gets token from storage or returns false
 * @return token || boolean
 */
OAuthProvider.prototype.getToken = function getToken() {
  const storage = this.store.get(this.options.tokenName);
  if (!storage) {
    return false;
  }
  if (!storage.accessToken) {
    return false;
  }
  return storage.accessToken;
};

export default OAuthProvider;
